import { FormEvent, useEffect, useId, useState } from "react";
import { ApiError } from "../../api/client";
import {
  buildBoardBugReportPayload,
  type BoardBugReportContext,
  type BoardBugReportKind,
} from "../../lib/board/boardBugReportDiagnostics";
import { submitBoardBugReport } from "../../lib/board/bugReportSubmit";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId?: string;
  defaultReporterName?: string;
  context?: BoardBugReportContext;
  onSuccess?: (reportId: string) => void;
  onError?: (message: string) => void;
};

/**
 * Обратная связь с доски — через API → Formspree (как звонилка).
 * Логи и ссылка на доску подставляются автоматически.
 */
export function BoardBugReportDialog({
  open,
  onOpenChange,
  boardId,
  defaultReporterName = "",
  context,
  onSuccess,
  onError,
}: Props) {
  const titleId = useId();
  const kindGroupId = useId();
  const [message, setMessage] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [reportKind, setReportKind] = useState<BoardBugReportKind>("problem");
  const [submitting, setSubmitting] = useState(false);
  const [localErr, setLocalErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setLocalErr("");
    setReporterName((prev) => (prev.trim() ? prev : defaultReporterName.trim()));
  }, [open, defaultReporterName]);

  useEffect(() => {
    if (!open) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onOpenChange(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 10) {
      setLocalErr("Опишите подробнее (хотя бы пару предложений).");
      return;
    }
    setLocalErr("");
    setSubmitting(true);
    try {
      const payload = buildBoardBugReportPayload({
        userMessage: trimmed,
        reporterName,
        boardId,
        reportKind,
        context,
      });
      const data = await submitBoardBugReport(payload);
      setMessage("");
      onOpenChange(false);
      onSuccess?.(data.id || "");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Не удалось отправить. Попробуйте позже.";
      setLocalErr(msg === "Failed to fetch" ? "Нет связи с сервером. Обновите страницу и попробуйте снова." : msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="wb-board-help" role="presentation">
      <button
        type="button"
        className="wb-board-help__backdrop"
        aria-label="Закрыть"
        onClick={() => onOpenChange(false)}
      />
      <div
        className="wb-board-help__dialog wb-bug-report-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="wb-board-help__head">
          <h2 id={titleId} className="wb-board-help__title">
            Сообщить о проблеме или предложить идею
          </h2>
          <button
            type="button"
            className="wb-board-help__close"
            aria-label="Закрыть"
            onClick={() => onOpenChange(false)}
          >
            ×
          </button>
        </header>
        <form className="wb-board-help__body wb-bug-report-dialog__form" onSubmit={onSubmit}>
          <p className="wb-board-help__lead">
            Опишите проблему или идею. Вместе с текстом отправятся ссылка на доску, состояние
            редактора и последние строки технического лога из браузера.
          </p>
          <fieldset className="wb-bug-report-dialog__kind" aria-labelledby={kindGroupId}>
            <legend id={kindGroupId} className="wb-bug-report-dialog__label">
              Тип обращения
            </legend>
            <div className="wb-bug-report-dialog__kind-row">
              <label className="wb-bug-report-dialog__kind-option">
                <input
                  type="radio"
                  name="wb-bug-kind"
                  value="problem"
                  checked={reportKind === "problem"}
                  onChange={() => setReportKind("problem")}
                  disabled={submitting}
                />
                Проблема
              </label>
              <label className="wb-bug-report-dialog__kind-option">
                <input
                  type="radio"
                  name="wb-bug-kind"
                  value="idea"
                  checked={reportKind === "idea"}
                  onChange={() => setReportKind("idea")}
                  disabled={submitting}
                />
                Идея
              </label>
            </div>
          </fieldset>
          <label className="wb-bug-report-dialog__label" htmlFor="wb-bug-name">
            Как к вам обращаться (необязательно)
          </label>
          <input
            id="wb-bug-name"
            className="wb-input wb-bug-report-dialog__input"
            value={reporterName}
            onChange={(e) => setReporterName(e.target.value)}
            autoComplete="nickname"
            disabled={submitting}
          />
          <label className="wb-bug-report-dialog__label" htmlFor="wb-bug-message">
            {reportKind === "idea" ? "Ваша идея" : "Что случилось?"}
          </label>
          <textarea
            id="wb-bug-message"
            className="wb-input wb-bug-report-dialog__textarea"
            rows={6}
            required
            minLength={10}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              reportKind === "idea"
                ? "Например: добавить шаблон для биологии, быстрый экспорт в PDF…"
                : "Например: не сохраняется штрих, пропала доска, ошибка при загрузке PDF…"
            }
            disabled={submitting}
          />
          {localErr ? (
            <p className="wb-alert wb-alert--error" role="alert">
              {localErr}
            </p>
          ) : null}
          <footer className="wb-board-help__foot wb-bug-report-dialog__foot">
            <button
              type="button"
              className="wb-btn wb-btn--ghost"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              Отмена
            </button>
            <button type="submit" className="wb-btn wb-btn--primary" disabled={submitting}>
              {submitting ? "Отправка…" : "Отправить"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
